const WORKSTREAM_TERMINAL_STATUSES = new Set(['completed', 'declined', 'failed', 'cancelled']);
const TASK_TERMINAL_STATUSES = new Set(['completed', 'cancelled']);

const text = (value) => String(value || '').trim();

const timestamp = (value) => {
  if (!value) return null;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
};

const userId = (value = {}) => {
  const source = value || {};
  return text(
    source.user_id ||
    source.cognito_sub ||
    source.sub ||
    source.id,
  );
};

export function getCurrentUserId(user, partnerProfile) {
  return userId(partnerProfile) || userId(user);
}

export function getBusinessUnitLabel(unit = {}) {
  const parent = unit?.parent_partner?.display_name || unit?.parent_partner?.partner_code;
  const name = unit?.display_name || unit?.business_unit_code || unit?.business_unit_id || 'Business Unit';
  return parent ? `${parent} / ${name}` : name;
}

export function getUserLabel(user = {}) {
  return text(user.full_name || user.email || user.user_id || user.cognito_sub) || 'AMX user';
}

function activeLead(workstream = {}) {
  const summaries = [
    workstream.assignment,
    workstream.active_assignment,
    workstream.request_capability?.assignment,
  ];
  for (const summary of summaries) {
    const lead = summary?.lead;
    if (lead && lead.active !== false && !lead.released_at) return lead;
  }

  const assignments = [
    ...(Array.isArray(workstream.assignments) ? workstream.assignments : []),
    ...(Array.isArray(workstream.request_capability?.assignments)
      ? workstream.request_capability.assignments
      : []),
  ];
  return assignments.find((assignment) => (
    assignment?.role === 'lead' && assignment?.active !== false && !assignment?.released_at
  )) || null;
}

export function getWorkstreamOwnerId(workstream = {}) {
  const lead = activeLead(workstream);
  if (lead) return userId(lead) || userId(lead.user);

  const responsible = workstream.responsible_user || workstream.request_capability?.responsible_user;
  return userId(responsible);
}

export function getTaskAssigneeId(task = {}) {
  return text(task.assignee_user_id || task.assignee_cognito_sub || task.assigned_to) || userId(task.assignee);
}

export function isWorkstreamOwnedByUser(workstream, currentUserId) {
  const ownerId = getWorkstreamOwnerId(workstream);
  return Boolean(ownerId && text(currentUserId) && ownerId === text(currentUserId));
}

export function isTaskAssignedToUser(task, currentUserId) {
  const assigneeId = getTaskAssigneeId(task);
  return Boolean(assigneeId && text(currentUserId) && assigneeId === text(currentUserId));
}

export function isWorkstreamTerminal(status) {
  return WORKSTREAM_TERMINAL_STATUSES.has(text(status).toLowerCase());
}

export function isTaskTerminal(status) {
  return TASK_TERMINAL_STATUSES.has(text(status).toLowerCase());
}

export function canContainPersonalWork(summary = {}) {
  const status = text(summary.status).toLowerCase();
  return Boolean(
    summary.request_capability_id &&
    !['pending_provider_response', 'declined'].includes(status),
  );
}

export function normalizeMyWorkstream(source = {}) {
  const request = source.request || {};
  const requestCapability = source.request_capability || source;
  const snapshot = requestCapability.capability_snapshot || source.capability_snapshot || {};
  const tasks = Array.isArray(source.tasks)
    ? source.tasks
    : Array.isArray(requestCapability.tasks) ? requestCapability.tasks : [];
  const owner = activeLead(source);
  const providerBusinessUnit = source.provider_business_unit || requestCapability.provider_business_unit || {};
  const requestingBusinessUnit = source.requesting_business_unit || requestCapability.requesting_business_unit || {};

  return {
    kind: 'workstream',
    requestCapabilityId: text(
      source.request_capability_id || requestCapability.request_capability_id,
    ),
    requestId: text(source.request_id || request.request_id || requestCapability.request_id),
    workspaceId: text(source.workspace_id || requestCapability.workspace_id || request.workspace_id),
    capabilityDefinitionId: text(
      source.capability_definition_id ||
      requestCapability.capability_definition_id ||
      source.capability?.capability_definition_id,
    ),
    capabilityName: text(
      source.capability_name || snapshot.capability_name || source.capability?.capability_name,
    ) || 'Requested Capability',
    requestTitle: text(source.title || request.title) || 'Untitled Request',
    status: text(source.status || requestCapability.status).toLowerCase() || 'unknown',
    providerBusinessUnit,
    providerBusinessUnitId: text(providerBusinessUnit.business_unit_id),
    providerBusinessUnitLabel: getBusinessUnitLabel(providerBusinessUnit),
    requestingBusinessUnit,
    requestingBusinessUnitLabel: getBusinessUnitLabel(requestingBusinessUnit),
    ownerId: getWorkstreamOwnerId(source),
    owner: owner?.user || source.responsible_user || requestCapability.responsible_user || null,
    claimedAt: owner?.assigned_at || source.claimed_at || requestCapability.claimed_at || '',
    openTaskCount: tasks.filter((task) => !isTaskTerminal(task.status)).length,
    totalTaskCount: tasks.length,
    createdAt: source.created_at || requestCapability.created_at || '',
    updatedAt: source.updated_at || requestCapability.updated_at || request.updated_at || '',
  };
}

export function normalizeMyTask(task = {}, source = {}) {
  const workstream = normalizeMyWorkstream(source);
  const expectedOutput = task.expected_output_definition || task.expected_output || {};
  const createdBy = task.created_by || {};
  return {
    kind: 'task',
    taskId: text(task.workstream_task_id || task.task_id || task.id),
    requestCapabilityId: text(task.request_capability_id) || workstream.requestCapabilityId,
    requestId: workstream.requestId,
    workspaceId: workstream.workspaceId,
    title: text(task.title || task.name) || 'Untitled Task',
    description: text(task.description),
    status: text(task.status).toLowerCase() || 'unknown',
    assigneeId: getTaskAssigneeId(task),
    assignee: task.assignee || null,
    createdById: text(task.created_by_user_id) || userId(createdBy),
    createdBy,
    createdByLabel: getUserLabel(createdBy),
    dueAt: task.due_at || task.due_date || '',
    expectedOutputName: text(expectedOutput.name || expectedOutput.label || expectedOutput.output_name),
    expectedOutputKind: text(expectedOutput.kind || expectedOutput.type),
    workstreamName: workstream.capabilityName,
    requestTitle: workstream.requestTitle,
    providerBusinessUnit: workstream.providerBusinessUnit,
    providerBusinessUnitId: workstream.providerBusinessUnitId,
    providerBusinessUnitLabel: workstream.providerBusinessUnitLabel,
    createdAt: task.created_at || '',
    updatedAt: task.updated_at || task.created_at || '',
  };
}

export function buildMyWork(details = [], currentUserId) {
  const workstreams = new Map();
  const tasks = new Map();

  details.forEach((detail) => {
    if (!detail || typeof detail !== 'object') return;
    const normalizedWorkstream = normalizeMyWorkstream(detail);
    if (
      normalizedWorkstream.requestCapabilityId &&
      isWorkstreamOwnedByUser(detail, currentUserId)
    ) {
      workstreams.set(normalizedWorkstream.requestCapabilityId, normalizedWorkstream);
    }

    const childTasks = Array.isArray(detail.tasks)
      ? detail.tasks
      : Array.isArray(detail.request_capability?.tasks) ? detail.request_capability.tasks : [];
    childTasks.forEach((task) => {
      if (!isTaskAssignedToUser(task, currentUserId)) return;
      const normalizedTask = normalizeMyTask(task, detail);
      if (normalizedTask.taskId) tasks.set(normalizedTask.taskId, normalizedTask);
    });
  });

  return {
    workstreams: sortMyWorkstreams([...workstreams.values()]),
    tasks: sortMyTasks([...tasks.values()]),
  };
}

export function sortMyWorkstreams(items = []) {
  return [...items].sort((left, right) => {
    const lifecycle = Number(isWorkstreamTerminal(left.status)) - Number(isWorkstreamTerminal(right.status));
    if (lifecycle) return lifecycle;
    const recent = (timestamp(right.updatedAt) || 0) - (timestamp(left.updatedAt) || 0);
    if (recent) return recent;
    return left.capabilityName.localeCompare(right.capabilityName);
  });
}

export function sortMyTasks(items = []) {
  return [...items].sort((left, right) => {
    const lifecycle = Number(isTaskTerminal(left.status)) - Number(isTaskTerminal(right.status));
    if (lifecycle) return lifecycle;

    if (!isTaskTerminal(left.status)) {
      const leftDue = timestamp(left.dueAt);
      const rightDue = timestamp(right.dueAt);
      if (leftDue !== rightDue) {
        if (leftDue === null) return 1;
        if (rightDue === null) return -1;
        return leftDue - rightDue;
      }
    }

    const recent = (timestamp(right.updatedAt) || 0) - (timestamp(left.updatedAt) || 0);
    if (recent) return recent;
    return left.title.localeCompare(right.title);
  });
}

const containsSearch = (item, search, fields) => {
  const query = text(search).toLowerCase();
  if (!query) return true;
  return fields.some((field) => text(item[field]).toLowerCase().includes(query));
};

const matchesLifecycle = (status, view, terminalCheck) => {
  if (view === 'all') return true;
  return view === 'completed'
    ? text(status).toLowerCase() === 'completed'
    : !terminalCheck(status);
};

export function filterMyWorkstreams(items = [], filters = {}) {
  return sortMyWorkstreams(items.filter((item) => (
    matchesLifecycle(item.status, filters.view || 'active', isWorkstreamTerminal) &&
    (!filters.businessUnitId || item.providerBusinessUnitId === filters.businessUnitId) &&
    (!filters.capabilityDefinitionId || item.capabilityDefinitionId === filters.capabilityDefinitionId) &&
    containsSearch(item, filters.search, [
      'capabilityName',
      'requestTitle',
      'providerBusinessUnitLabel',
      'requestingBusinessUnitLabel',
      'status',
    ])
  )));
}

export function filterMyTasks(items = [], filters = {}) {
  return sortMyTasks(items.filter((item) => (
    matchesLifecycle(item.status, filters.view || 'active', isTaskTerminal) &&
    (!filters.businessUnitId || item.providerBusinessUnitId === filters.businessUnitId) &&
    (!filters.workstreamId || item.requestCapabilityId === filters.workstreamId) &&
    containsSearch(item, filters.search, [
      'title',
      'description',
      'workstreamName',
      'requestTitle',
      'providerBusinessUnitLabel',
      'expectedOutputName',
      'expectedOutputKind',
      'createdByLabel',
      'status',
    ])
  )));
}

export function buildMyWorkNavigation(item = {}) {
  const workspaceId = text(item.workspaceId || item.workspace_id);
  if (!workspaceId) return null;

  const requestCapabilityId = text(item.requestCapabilityId || item.request_capability_id);
  const taskId = text(item.taskId || item.workstream_task_id || item.task_id);
  const params = new URLSearchParams();
  if (requestCapabilityId) params.set('workstream', requestCapabilityId);
  if (taskId) params.set('task', taskId);
  const query = params.toString();

  return {
    path: `/workspaces/${encodeURIComponent(workspaceId)}${query ? `?${query}` : ''}`,
    state: {
      from: '/my-work',
      openWork: true,
      requestCapabilityId,
      taskId: taskId || undefined,
    },
  };
}
